import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LessonContent } from '../../types/lesson';
import { useAuth } from '../../context/AuthContext';
import { useStatsRefresh } from '../../context/StatsRefreshContext';
import { useLessonPersistedState } from './useLessonPersistedState';
import { useLanguageCode } from '../../hooks/useLanguageCode';
import { lessonService } from '../../api/lessonService';
import WordClickPopup, { getWordAtPoint } from '../word/WordClickPopup';
import WarmUpSection from './sections/WarmUpSection';
import DiscoveryCardsSection from './sections/DiscoveryCardsSection';
import CollocationDiscoverySection from './sections/CollocationDiscoverySection';
import MCQSection from './sections/MCQSection';
import WordFormationSection from './sections/WordFormationSection';
import ErrorDetectiveSection from './sections/ErrorDetectiveSection';
import SpeakingChallengeSection from './sections/SpeakingChallengeSection';
import RapidFireSection from './sections/RapidFireSection';
import '../../styles/lesson.css';

const TEACHER_MODE_STORAGE_KEY = 'weavewords_teacher_mode';

interface Props {
    /** Absent on the public share page (PublicLessonPage) — progress there is never persisted
     *  (see useLessonPersistedState), matching readOnly's "nothing to save" semantics. */
    lessonId?: number;
    title: string;
    content: LessonContent;
    /** True on the public share page — hides nothing structurally different today, but signals
     *  intent for future owner-only affordances (edit/delete) that must never appear there. */
    readOnly?: boolean;
    /** The lesson's own language as stored — a display name like "French" (converted to its code
     *  via useLanguageCode). May differ from the profile's learning language, since the creation
     *  form lets the user pick any. Used for translation and word lookup; absent → the profile's
     *  learning language. Completion resolves it server-side. */
    language?: string;
    /** Server-side completion state (Lesson.completed_at/xp_earned/reviewed_word_count) — survives
     *  "Restart" and other devices, unlike the browser-local progress. */
    completion?: { completedAt?: string | null; xpEarned?: number; reviewedWordCount?: number };
}

interface WordPopupState {
    word: string;
    x: number;
    y: number;
}

const LessonPlayer: React.FC<Props> = ({ lessonId, title, content, readOnly, language, completion }) => {
    const { t } = useTranslation('lesson');
    const navigate = useNavigate();
    const auth = useAuth();
    const { refreshStats } = useStatsRefresh();
    const [current, setCurrent] = useLessonPersistedState(lessonId, 'player.current', 0);
    const [teacherMode, setTeacherMode] = useState(false);
    const [wordPopup, setWordPopup] = useState<WordPopupState | null>(null);

    // Aggregates every answered question across every section into one word -> result map, keyed
    // by lowercased word text — a word tested more than once (e.g. recycled across sections)
    // naturally collapses to a single entry (last answer wins), which is exactly what "Finish
    // Lesson" needs: submit at most one real SM-2 review per distinct word, never twice for the
    // same word in one Finish action (see LessonProgressService's dedup requirement). hintUsed
    // marks that the learner opened a translate icon on one of the item's core content fields
    // (sentence/stem/options/etc.) before answering — a legitimate peek that still earns credit,
    // but at a reduced quality/XP tier (see handleFinish).
    const [wordResults, setWordResults] = useLessonPersistedState<Record<string, { correct: boolean; hintUsed: boolean }>>(lessonId, 'wordResults', {});
    const [xpAwarded, setXpAwarded] = useLessonPersistedState(lessonId, 'xpAwarded', false);
    const [xpEarned, setXpEarned] = useLessonPersistedState(lessonId, 'xpEarned', 0);
    const [reviewedWordCount, setReviewedWordCount] = useLessonPersistedState(lessonId, 'reviewedWordCount', 0);
    const [finishing, setFinishing] = useState(false);
    const [finishError, setFinishError] = useState<string | null>(null);
    // True when the server says this lesson was already completed before — e.g. after Restart,
    // or on another device. Finishing again awards nothing, so the pill says so.
    const [alreadyCompleted, setAlreadyCompleted] = useState(false);
    const completedOnServer = !!completion?.completedAt;
    // `language` is the lesson's stored display name ("French"); everything below needs the code.
    // Falls back to the profile's learning language only when the lesson has no language. One
    // that can't be resolved (unknown name, or the supported-languages fetch failed) stays
    // undefined: guessing the profile language would file a German lesson's words as French.
    const resolvedLessonLanguage = useLanguageCode(language);
    const lessonLanguage = language ? resolvedLessonLanguage : auth?.userSettings?.learning_language;

    const handleWordAnswered = (word: string, correct: boolean, hintUsed?: boolean) => {
        setWordResults((prev) => ({ ...prev, [word.toLowerCase()]: { correct, hintUsed: !!hintUsed } }));
    };

    // The server computes the XP and submits the SM-2 reviews, once per lesson (see
    // lessonService.complete) — so a retry after a failed request, a Restart, or finishing on a
    // second device can never award XP or reviews twice. hintUsed marks that the learner opened
    // a translate icon on one of the item's core content fields before answering, which the
    // server scores at a reduced quality/XP tier.
    const handleFinish = async () => {
        if (xpAwarded || finishing || !lessonId) return;
        setFinishing(true);
        setFinishError(null);
        try {
            const results = Object.entries(wordResults).map(([word, r]) => ({ word, correct: r.correct, hint_used: r.hintUsed }));
            const result = await lessonService.complete(lessonId, results, auth?.userSettings?.learning_language);
            if (results.length > 0 && !result.already_completed) refreshStats();
            setXpEarned(result.xp_earned);
            setReviewedWordCount(result.reviewed_word_count);
            setAlreadyCompleted(result.already_completed);
            setXpAwarded(true);
            if (!readOnly && !result.already_completed) navigate('/lessons');
        } catch {
            setFinishError(t('player.finish_error', 'Could not save your results. Please try again.'));
        } finally {
            setFinishing(false);
        }
    };

    // Lets a learner click any word in the lesson's text and add it to their own vocabulary,
    // the same click-a-word-in-plain-text mechanism addWord.tsx/editWord.tsx already use (see
    // WordClickPopup.tsx) — no per-word markup needed, it works on any text node under the click.
    // Disabled on the public share page (readOnly): an anonymous visitor has no vocabulary of
    // their own to add to, and WordClickPopup's fallback would just bounce them to a login wall.
    const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (readOnly) return;
        // Don't hijack clicks on actual interactive controls (answer options, inputs, etc.) —
        // those already have their own onClick handling and must not also open a word popup.
        if ((e.target as HTMLElement).closest('button, input, textarea, select, a')) return;
        // Whitelist, not blacklist: only the lesson's own language content (sentences, examples,
        // explanations, prompts, ...) opts into word-click via `.lesson-word-target` — interface
        // chrome (section titles, "Question N"/"Sentence N" labels, kickers, descriptions, score
        // pills) never does, even though it's plain text sitting in the same clicked area.
        if (!(e.target as HTMLElement).closest('.lesson-word-target')) {
            setWordPopup(null);
            return;
        }
        const word = getWordAtPoint(e.clientX, e.clientY);
        if (word) setWordPopup({ word, x: e.clientX, y: e.clientY });
        else setWordPopup(null);
    };

    useEffect(() => {
        try {
            setTeacherMode(localStorage.getItem(TEACHER_MODE_STORAGE_KEY) === '1');
        } catch {
            // ignore — teacher mode just defaults to off
        }
    }, []);

    const toggleTeacherMode = () => {
        const next = !teacherMode;
        setTeacherMode(next);
        try {
            localStorage.setItem(TEACHER_MODE_STORAGE_KEY, next ? '1' : '0');
        } catch {
            // ignore — not persisted this session, harmless
        }
    };

    // Excludes any section the creation form's picker left out — those come back from
    // generation as an empty array (see weavewords-api's lessonJSONSchema), so filtering on
    // length here is enough to skip them without needing to know the original picker selection.
    const learningLanguage = lessonLanguage;
    const nativeLanguage = auth?.userSettings?.native_language;

    const allSections = [
        { label: t('steps.warm_up', '1 · Warm-up'), items: content.warm_up, render: () => <WarmUpSection lessonId={lessonId} items={content.warm_up} teacherMode={teacherMode} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
        { label: t('steps.discover', '2 · Discover'), items: content.discovery_cards, render: () => <DiscoveryCardsSection lessonId={lessonId} items={content.discovery_cards} teacherMode={teacherMode} onWordAnswered={handleWordAnswered} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
        { label: t('steps.collocations', '3 · Collocations'), items: content.collocation_discovery, render: () => <CollocationDiscoverySection lessonId={lessonId} items={content.collocation_discovery} teacherMode={teacherMode} onWordAnswered={handleWordAnswered} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
        { label: t('steps.mcq', '4 · Choose'), items: content.mcq_items, render: () => <MCQSection lessonId={lessonId} items={content.mcq_items} teacherMode={teacherMode} onWordAnswered={handleWordAnswered} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
        { label: t('steps.wordform', '5 · Word Formation'), items: content.word_formation_items, render: () => <WordFormationSection lessonId={lessonId} items={content.word_formation_items} teacherMode={teacherMode} onWordAnswered={handleWordAnswered} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
        { label: t('steps.errors', '6 · Error Detective'), items: content.error_detective_items, render: () => <ErrorDetectiveSection lessonId={lessonId} items={content.error_detective_items} teacherMode={teacherMode} onWordAnswered={handleWordAnswered} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
        { label: t('steps.speak', '7 · Speaking'), items: content.speaking_situations, render: () => <SpeakingChallengeSection lessonId={lessonId} items={content.speaking_situations} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
        { label: t('steps.rapid', '8 · Use It'), items: content.rapid_fire_prompts, render: () => <RapidFireSection lessonId={lessonId} items={content.rapid_fire_prompts} targetVocabulary={content.target_vocabulary} sourceLanguage={learningLanguage} targetLanguage={nativeLanguage} /> },
    ];
    const sections = allSections.filter((s) => s.items && s.items.length > 0);
    const activeIndex = Math.min(current, Math.max(sections.length - 1, 0));

    // Proportional rather than the old fixed 8-way milestone table — this lesson may only have,
    // say, 3 of the 8 sections (per the creation form's picker), so "step 2 of 3" should read as
    // meaningfully further along than "step 2 of 8" would.
    const pct = sections.length > 0 ? Math.round(((activeIndex + 1) / sections.length) * 100) : 0;

    if (sections.length === 0) {
        return (
            <div className="lesson-player">
                <div className="lesson-player-error">{t('player.no_sections', 'This lesson has no content to show.')}</div>
            </div>
        );
    }

    return (
        <div className="lesson-player">
            <div className="lesson-player-header">
                <div className="lesson-player-header-row">
                    <h1>{title}</h1>
                    <div className={`lesson-teacher-toggle${teacherMode ? ' on' : ''}`} role="button" tabIndex={0} onClick={toggleTeacherMode}>
                        <span className="dot" />
                        <span>{t('teacher_mode', 'Teacher mode')}</span>
                    </div>
                </div>
                <div className="lesson-progress-track">
                    <div className="lesson-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="lesson-progress-labels">
                    <span>0%</span><span>20%</span><span>40%</span><span>60%</span><span>80%</span><span>100%</span>
                </div>
                <div className="lesson-steps">
                    {sections.map((s, i) => (
                        <button
                            key={s.label}
                            className={`lesson-step-btn${i === activeIndex ? ' active' : ''}`}
                            onClick={() => setCurrent(i)}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            <div onClick={readOnly ? undefined : handleContentClick}>
                {sections[activeIndex].render()}
            </div>

            <div className="lesson-nav-footer">
                <button className="lesson-btn ghost" disabled={activeIndex === 0} onClick={() => setCurrent((c) => c - 1)}>
                    {t('back', '← Back')}
                </button>
                {!readOnly && activeIndex === sections.length - 1 ? (
                    xpAwarded && !alreadyCompleted ? (
                        <div className="lesson-score-pill">
                            {xpEarned > 0
                                ? t('player.completed_with_xp', '🎉 Lesson complete — +{{xp}} XP, {{words}} words reviewed', { xp: xpEarned, words: reviewedWordCount })
                                : t('player.completed', '✓ Lesson complete')}
                        </div>
                    ) : xpAwarded || completedOnServer ? (
                        <div className="lesson-score-pill">
                            {t('player.completed_before', '✓ Lesson complete — you already earned the XP for this lesson')}
                        </div>
                    ) : (
                        <button className="lesson-btn" onClick={handleFinish} disabled={finishing}>
                            {finishing ? t('player.finishing', 'Saving…') : t('player.finish', 'Finish Lesson')}
                        </button>
                    )
                ) : (
                    <button
                        className="lesson-btn"
                        disabled={activeIndex === sections.length - 1}
                        onClick={() => setCurrent((c) => Math.min(sections.length - 1, c + 1))}
                    >
                        {t('next', 'Next →')}
                    </button>
                )}
            </div>
            {finishError && <div className="lesson-player-error">{finishError}</div>}

            {!readOnly && (
                <WordClickPopup
                    popup={wordPopup}
                    onClose={() => setWordPopup(null)}
                    learningLanguage={lessonLanguage}
                />
            )}
        </div>
    );
};

export default LessonPlayer;
